import React, { memo, useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { OrcamentoConcluido } from "./orcamentoConcluido"
import { ModalImprimirContainer, ParametrosTemplate, PermissaoSenhaModalImprimirProps } from "./modal-imprimir/modalImprimirContainer"
import { FiltrosListarTemplateSeguranca, PermissoesImpressao } from "../../../types/modalImprimir/RespostaModalImprimir"
import globalMU from "../../../../globalMU"
import { tratamentoErroPadrao } from "../../../use-cases"
import microuniversoApi from "../../../api/microuniversoApi"
import { IOrcamento } from "../../../types/IOrcamento"
import { obterParametrosPorOidItem } from 'app/use-cases/parametro/obterParametros'
import { ParametroPorItem } from 'app/types/Parametro'
import { Vendedor } from 'app/types/Vendedor'
import store from 'app/redux/store/store'
import { limparOrcamentoEmAndamentoAction } from 'app/redux/orcamentoEmAndamentoSlice'
import { limparProdutosSelecionadosAction } from 'app/redux/produtosSelecionadosSlice'

export type OrcamentoConcluidoContainerProps = {
    orcamento: IOrcamento;
    vendedorLogado?: Vendedor;
}

declare var window: any

const _SIGLA_FUNCIONALIDADE_ORCAMENTO_FINALIZAR = "ORCAMENTO_FINALIZAR"
const _ACAO_IMPRIMIR_ORCAMENTO = "IMPRIMIR_ORCAMENTO"
const _PARAMETRO_SOLICITAR_AUTORIZACAO_IMPRESSAO = "SOLICITAR_AUTORIZACAO_IMPRESSAO_ORCAMENTO_DESCONTO_ACIMA_PERMITIDO"
const _MENSAGEM_ORCAMENTO_NAO_IMPRIME = "Situação do orçamento impede que seja impresso"

const _ACOES_PERMISSOES = [
    _ACAO_IMPRIMIR_ORCAMENTO,
    "PERMITIR_ENVIAR_EMAIL",
    "PERMITIR_IMPRIMIR_WORD",
    "PERMITIR_ALTERAR_ASSUNTO_EMAIL",
    "PERMITIR_ALTERAR_COPIA_EMAIL",
    "PERMITIR_ALTERAR_COPIA_OCULTA_EMAIL",
    "PERMITIR_ALTERAR_MENSAGEM_EMAIL",
    "PERMITIR_ANEXAR_ARQUIVOS_EMAIL"
]

type EstadoPermissoesImpressao = {
    permitidoImprimir: boolean
    permissoesImpressao: PermissoesImpressao
}

export const OrcamentoConcluidoContainer = memo((props: OrcamentoConcluidoContainerProps) => {
    const [imprimir, setImprimir] = useState<boolean>(false)
    const [oidUsuarioLogado, setOidUsuarioLogado] = useState<number>(0)
    const [estadoPermissoes, setEstadoPermissoes] = useState<EstadoPermissoesImpressao>({
        permitidoImprimir: false,
        permissoesImpressao: {}
    })
    const [parametrosPorFilial, setParametrosPorFilial] = useState<ParametroPorItem[] | null>(
        props.orcamento.OidFilialOrcamento > 7 ? null : []
    ) // 450413
    const [processando, setProcessando] = useState<boolean>(false)  
    const isMounted = useRef<boolean>(true)


    const token = useMemo(() => globalMU.token(), [])

    useEffect(() => () => {
        isMounted.current = false
    }, [])

    const obterPermissao = useCallback(async () => {
        const permissoes = await Promise.all(
            _ACOES_PERMISSOES.map(acao =>
                microuniversoApi.PermissaoFuncionalidade.obterUsuarioPermitidoFuncionalidadeAcao(
                    _SIGLA_FUNCIONALIDADE_ORCAMENTO_FINALIZAR, [acao]
                )
            )
        )

        if (!isMounted.current) return

        setEstadoPermissoes({
            permitidoImprimir: permissoes[0],
            permissoesImpressao: {
                permitidoEnviarEmail: permissoes[1],
                permitidoImprimirWord: permissoes[2],
                permitidoAlterarCopiaEmail: permissoes[3],
                permitidoAlterarCopiaOcultaEmail: permissoes[4],
                permitidoAlterarAssuntoEmail: permissoes[5],
                permitidoAlterarMensagemEmail: permissoes[6],
                permitidoAnexarEmail: permissoes[7]
            }
        })
    }, [])

    const atualizarParametrosPorFilial = useCallback(async () => {
        const parametros = await obterParametrosPorOidItem({
            nomes: [_PARAMETRO_SOLICITAR_AUTORIZACAO_IMPRESSAO],
            oidItem: props.orcamento.OidFilialOrcamento
        })

        if (!isMounted.current) return

        setParametrosPorFilial(parametros)
    }, [props.orcamento.OidFilialOrcamento])

    const obterOidUsuario = useCallback(async () => {
        try {
            globalMU.blockUI()
            const oidUsuario = await microuniversoApi.Usuario.obterOid()

            if (!isMounted.current) return

            setOidUsuarioLogado(oidUsuario)
        } catch (erro) {
            tratamentoErroPadrao(erro)
        } finally {
            globalMU.unblockUI()
        }
    }, [])

    const filtrosTemplates = useMemo<FiltrosListarTemplateSeguranca>(() => ({
        token: encodeURIComponent(token),
        codigoUsuario: oidUsuarioLogado,
        siglaFuncionalidade: _SIGLA_FUNCIONALIDADE_ORCAMENTO_FINALIZAR,
        siglaAcao: _ACAO_IMPRIMIR_ORCAMENTO,
        codigoFilial: props.orcamento.CodigoFilialOrcamento,
    }), [token, oidUsuarioLogado, props.orcamento.CodigoFilialOrcamento])

    const permissaoSenhaModalImprimir = useMemo<PermissaoSenhaModalImprimirProps>(() => { // 450413
        if (parametrosPorFilial == null || parametrosPorFilial.length === 0) {
            return null
        }

        const possuiPendencia = props.orcamento.PossuiPendenciaDesconto
        const precisaSenha = parametrosPorFilial.some(
            (parametro) => parametro.Nome === _PARAMETRO_SOLICITAR_AUTORIZACAO_IMPRESSAO && parametro.Valor === "S"
        )

        return {
            senhaSolicitar: precisaSenha && possuiPendencia ? "TIPO_LIBERACAO_LIBORCDESCONTO" : undefined,
            senhaSolicitarDica: precisaSenha && possuiPendencia ? "Permissao: [Permitir imprimir orçamentos com desconto acima do permitido]" : undefined,
            senhaTextoAdicional: precisaSenha && possuiPendencia ? "Pedido Possui Pendência de Desconto" : undefined
        }
    }, [parametrosPorFilial, props.orcamento.PossuiPendenciaDesconto])

    const parametrosTemplate = useMemo<ParametrosTemplate[]>(() => [
        {
            nome: "id",
            valor: [props.orcamento.Numero],
        },
    ], [props.orcamento.Numero])

    const nomeArquivoWord = useMemo(() => `ORC${props.orcamento.Numero}.doc`, [props.orcamento.Numero])

    useEffect(() => {
        sessionStorage.setItem('orcamentoConcluido', "true") // 467485 /458014

        // 491123 — limpa ao ENTRAR nesta tela (não mais no cleanup/unmount): o orçamento foi
        // concluído com sucesso, então qualquer snapshot pendente (orcamentoEmAndamento/cesta de
        // produtos) já não faz mais sentido oferecer para recuperação depois. Limpar aqui (em vez
        // de esperar o unmount) garante que a limpeza acontece mesmo que o navegador seja fechado
        // enquanto esta tela ainda está aberta — o cleanup de useEffect nunca roda nesse caso.
        //
        // 493082 -NÃO limpa store.orcamento (limparInformacoesOrcamentoAction) aqui — o Totalizador
        // (TotalizadorContainer.tsx) fica montado em #totalizador-react durante toda esta tela
        // (fora de #conteudo-pagina, nunca desmonta na troca Finalizar -> Concluído — ver
        // Views/Orcamento/Index.cshtml) e lê os totais direto desse mesmo estado; zerá-lo aqui
        // zerava a exibição do Totalizador sem necessidade. Quem precisa saber que o orçamento
        // já terminou (existeOrcamentoEmAndamento, beforeunload, menu principal) já checa a flag
        // sessionStorage 'orcamentoConcluido' setada acima antes de agir — não depende de
        // store.orcamento estar vazio. A limpeza de fato acontece ao sair do módulo (menu
        // principal/logout, ver MenuPrincipalContainer.tsx) ou é irrelevante porque toda saída
        // desta tela (Novo Orçamento/Editar Orçamento) é um reload de página inteira.
		// 493082 - store.dispatch(limparInformacoesOrcamentoAction()) // 467485 /458014
        store.dispatch(limparOrcamentoEmAndamentoAction())
        store.dispatch(limparProdutosSelecionadosAction())

        return () => {
            sessionStorage.removeItem('orcamentoConcluido') // 467485 /458014
        }
    }, [])

    useEffect(() => {
        $('#corpo_BaseInterno').css('margin-left', '0px')
        window.orcamentoController.flagOrcamentoFinalizado = true
        const executarFuncoes = async () => {
            try {
                await Promise.all([
                    obterOidUsuario(),
                    obterPermissao(),
                    microuniversoApi.Orcamento.RemoverControleExclusividade(props.orcamento.Numero ?? 0, true) // 458014
                ])
            } catch (erro) {
                tratamentoErroPadrao(erro)
            }
        }
    
        executarFuncoes()
    }, [obterOidUsuario, obterPermissao, props.orcamento.Numero])

    useEffect(() => {
        if (props.orcamento.OidFilialOrcamento > 7) {
            setParametrosPorFilial(null)
            atualizarParametrosPorFilial()
            return
        }

        setParametrosPorFilial([])
    }, [props.orcamento.OidFilialOrcamento, atualizarParametrosPorFilial])

    const handleClickNumeroOrc = useCallback(() => {
        globalMU.blockUI()
        setProcessando(true)
        //globalMU.showProcessingToast("Abrindo Orçamento", "Aguarde")
        window.location.href = `/Orcamento/EditarOrcamento?numeroOrcamento=${props.orcamento.Numero}&tipoOrcamento=${props.orcamento.TipoOrcamentoDescricao.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[-\s]/g, '')}&XAbaId=${sessionStorage.getItem('aba_id')}`
        globalMU.showProcessingToast("", "Abrindo Orçamento", true)
    }, [props.orcamento.Numero, props.orcamento.TipoOrcamentoDescricao])

    const handleNovoOrcamento = useCallback(() => {
        setProcessando(true)
        globalMU.blockUI()
        window.location.href = `/Orcamento/IncluirOrcamento?tipoOrcamento=0&XAbaId=${sessionStorage.getItem('aba_id')}`
    }, [])

    const handleImprimirOrcamento = useCallback(() => {
        props.orcamento.PermiteImprimir
            ? setImprimir(true)
            : globalMU.showInfoMessage(_MENSAGEM_ORCAMENTO_NAO_IMPRIME)
    }, [props.orcamento.PermiteImprimir])

    const handleCloseModalImprimir = useCallback(() => {
        setImprimir(false)
    }, [])

    return <>
    {parametrosPorFilial != null && oidUsuarioLogado > 0 && <>

            <OrcamentoConcluido
                permitidoImprimir={estadoPermissoes.permitidoImprimir}
                processando={processando}
                onClickNumeroOrc={handleClickNumeroOrc}
                numero={props.orcamento.Numero}
                descricaoSituacao={props.orcamento.DescricaoSituacao}
                aptoParaCheckoutMovel={props.orcamento.AptoParaCheckoutMovel}
                numeroDav={props.orcamento.CodigoDAV}
                onImprimirOrcamento={handleImprimirOrcamento}
                onNovoOrcamento={handleNovoOrcamento}
            />

            <ModalImprimirContainer
                filtrosTemplates={filtrosTemplates}
                nomeArquivoWord={nomeArquivoWord}
                urlBase="/Relatorio/Orcamento"
                parametrosTemplate={parametrosTemplate}
                permissaoSenhaModalImprimir={permissaoSenhaModalImprimir}
                exibirModalImprimir={imprimir}
                onClose={handleCloseModalImprimir}
                idOrcamento={props.orcamento.Numero}
                permissoes={estadoPermissoes.permissoesImpressao}
                vendedor={props.vendedorLogado}
            />
</>}
        </>
    }
)
